import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import API from '../utils/api';
import JobCard from '../components/JobCard';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import { useCountUp } from '../hooks/useCountUp';
import { CATEGORY_METADATA, PRIMARY_CATEGORIES, CATEGORIES } from '../constants/categories';
import {
  FaBriefcase,
  FaRocket,
  FaLaptopCode,
  FaHeartbeat,
  FaSearch,
  FaUsers
} from 'react-icons/fa';
import FAQSection from '../components/FAQSection';
import './Home.css';

const featuresData = [
  {
    id: 'feature-easy-apply',
    icon: (
      <svg width="28" height="28" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
        <path d="M9 12l2 2 4-4m6 2a9 9 0 1 1-18 0 9 9 0 0 1 18 0z"/>
      </svg>
    ),
    title: 'Easy Apply',
    desc: 'Apply to thousands of jobs with a single click using your TezTech profile - no repetitive forms.',
  },
  {
    id: 'feature-verified',
    icon: (
      <svg width="28" height="28" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
        <path d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 0 0 1.946-.806 3.42 3.42 0 0 1 4.438 0 3.42 3.42 0 0 0 1.946.806 3.42 3.42 0 0 1 3.138 3.138 3.42 3.42 0 0 0 .806 1.946 3.42 3.42 0 0 1 0 4.438 3.42 3.42 0 0 0-.806 1.946 3.42 3.42 0 0 1-3.138 3.138 3.42 3.42 0 0 0-1.946.806 3.42 3.42 0 0 1-4.438 0 3.42 3.42 0 0 0-1.946-.806 3.42 3.42 0 0 1-3.138-3.138 3.42 3.42 0 0 0-.806-1.946 3.42 3.42 0 0 1 0-4.438 3.42 3.42 0 0 0 .806-1.946 3.42 3.42 0 0 1 3.138-3.138z"/>
      </svg>
    ),
    title: 'Verified Companies',
    desc: 'All companies on TezTech Naukri are verified and trusted. Zero fake job postings, guaranteed.',
  },
  {
    id: 'feature-support',
    icon: (
      <svg width="28" height="28" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
        <path d="M17 20h5v-2a3 3 0 0 0-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 0 1 5.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 0 1 9.288 0M15 7a3 3 0 1 1-6 0 3 3 0 0 1 6 0zm6 3a2 2 0 1 1-4 0 2 2 0 0 1 4 0zM7 10a2 2 0 1 1-4 0 2 2 0 0 1 4 0z"/>
      </svg>
    ),
    title: 'Career Support',
    desc: 'Get expert guidance from career coaches and mentors to accelerate your professional growth.',
  },
];

const testimonials = [
  { name: "Priya Sharma", role: "Frontend Developer", company: "TechCorp", package: "₹18 LPA", text: "TezTech Naukri made my job search incredibly smooth. The UI is fantastic and I got hired within 2 weeks!", rating: 5, avatar: "PS", color: "#8b5cf6" },
  { name: "Rahul Verma", role: "Product Manager", company: "Innovate Ltd", package: "₹24 LPA", text: "The verified company tags gave me peace of mind. Highly recommend this platform to everyone.", rating: 5, avatar: "RV", color: "#00c2ab" },
  { name: "Sneha Patel", role: "Data Scientist", company: "DataSys", package: "₹22 LPA", text: "I found my dream job within a week of signing up! The matching algorithm is spot on.", rating: 5, avatar: "SP", color: "#ec4899" },
  { name: "Amit Kumar", role: "DevOps Engineer", company: "CloudNet", package: "₹20 LPA", text: "The platform's UI is beautiful and easy to use. Better than any other job portal I've used.", rating: 4, avatar: "AK", color: "#f59e0b" },
];

const Home = () => {
  const { isAuthenticated, user } = useAuth();
  const { settings } = useSettings();
  const [featuredJobs, setFeaturedJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [stats, setStats] = useState({ jobs: 0, companies: 0, users: 0 });
  const [startCounting, setStartCounting] = useState(false);
  const [categoryCounts, setCategoryCounts] = useState({});
  const [showAllCategories, setShowAllCategories] = useState(false);
  
  // Testimonials State
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  // Animated counters
  const jobsCount = useCountUp(stats.jobs, 2000, startCounting);
  const companiesCount = useCountUp(stats.companies, 2000, startCounting);
  const usersCount = useCountUp(stats.users, 2500, startCounting);

  // Build categories array from metadata
  const getCategoriesToDisplay = () => {
    if (showAllCategories) {
      return CATEGORIES.map(name => ({
        name,
        ...CATEGORY_METADATA[name]
      }));
    } else {
      return PRIMARY_CATEGORIES.map(name => ({
        name,
        ...CATEGORY_METADATA[name]
      }));
    }
  };

  const categoriesToDisplay = getCategoriesToDisplay();

  useEffect(() => {
    fetchFeaturedJobs();
    fetchStats();
    fetchCategoryCounts();
  }, []);

  const fetchFeaturedJobs = async () => {
    try {
      setLoading(true);
      // Fetch only featured jobs for homepage
      const response = await API.get('/jobs?featured=true&limit=8&sort=createdAt');
      // Sort jobs by createdAt descending (most recent first)
      const jobs = (response.data.jobs || []).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setFeaturedJobs(jobs);
    } catch (error) {
      console.error('Error fetching featured jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await API.get('/stats');
      setStats(response.data);
      // Start counting animation after stats are loaded
      setTimeout(() => setStartCounting(true), 300);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchCategoryCounts = async () => {
    try {
      const response = await API.get('/stats/categories');
      setCategoryCounts(response.data);
    } catch (error) {
      console.error('Error fetching category counts:', error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      window.location.href = `/jobs?search=${encodeURIComponent(searchQuery)}`;
    }
  };

  useEffect(() => {
    const obs = new IntersectionObserver(
      entries => entries.forEach(e => e.isIntersecting && e.target.classList.add('visible')),
      { threshold: 0.1 }
    );
    document.querySelectorAll('.reveal').forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, [categoriesToDisplay, featuredJobs, activeTestimonial]);

  return (
    <div className="min-h-screen">
      
      {/* ── HERO SECTION ── */}
      <section className="hero-wrapper">
        <div className="hero-shape-1"></div>
        <div className="hero-shape-2"></div>
        
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="hero-content">
            
            <div className="reveal">
              <div className="hero-badge">
                🚀 #1 Job Portal for Tech Professionals
              </div>
              <h1 className="hero-title">
                {settings.hero?.title || <>Find Your <span>Dream Job</span> Today</>}
              </h1>
              <p className="hero-subtitle">
                {settings.hero?.subtitle || 'Discover thousands of opportunities, connect with top companies, and accelerate your career with our AI-powered matching platform.'}
              </p>
              
              {/* Search Bar - Only show if enabled in settings */}
              {(settings.hero?.showSearchBar !== false) && (
                <form onSubmit={handleSearch} className="glass-search-box">
                  <input
                    type="text"
                    placeholder="Job title, keywords, or company..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  <button type="submit" className="glass-search-btn">
                    <FaSearch />
                    Search
                  </button>
                </form>
              )}

              {/* Quick Stats */}
              <div className="hero-stats">
                <div className="hero-stat-item">
                  <span className="hero-stat-num">{jobsCount}+</span>
                  <span className="hero-stat-label">Active Jobs</span>
                </div>
                <div className="hero-stat-item">
                  <span className="hero-stat-num">{companiesCount}+</span>
                  <span className="hero-stat-label">Companies</span>
                </div>
                <div className="hero-stat-item">
                  <span className="hero-stat-num">{usersCount}+</span>
                  <span className="hero-stat-label">Candidates</span>
                </div>
              </div>
            </div>

            <div className="hero-illustration reveal" style={{ transitionDelay: '0.2s' }}>
              <div className="floating-card card-1">
                <div className="float-icon"><FaLaptopCode /></div>
                <div className="float-info">
                  <h4>Software Engineer</h4>
                  <p>Google • ₹35 LPA</p>
                </div>
              </div>
              
              <div className="floating-card card-2">
                <div className="float-icon"><FaHeartbeat /></div>
                <div className="float-info">
                  <h4>Product Designer</h4>
                  <p>Spotify • ₹28 LPA</p>
                </div>
              </div>
              
              <div className="floating-card card-3">
                <div className="float-icon"><FaBriefcase /></div>
                <div className="float-info">
                  <h4>Data Scientist</h4>
                  <p>Amazon • ₹32 LPA</p>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ── FEATURES SECTION ── */}
      <section className="section-wrapper bg-white-section" id="features">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="section-head reveal">
            <h2 className="section-title">Why Choose <span className="text-teal">TezTech Naukri?</span></h2>
            <p className="section-sub">Everything you need to land your dream job, perfectly organized in one intuitive platform.</p>
          </div>

          <div className="features-grid">
            {featuresData.map((f, i) => (
              <div
                key={f.id}
                id={f.id}
                className="feature-card reveal"
                style={{ transitionDelay: `${i * 0.1}s` }}
              >
                <div className="feature-icon-box">{f.icon}</div>
                <h3>{f.title}</h3>
                <p>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CATEGORIES SECTION ── */}
      <section className="section-wrapper bg-gray-section" id="categories">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="categories-head reveal">
            <div>
              <h2 className="section-title">Browse by <span className="text-teal">Category</span></h2>
              <p className="section-sub" style={{ margin: 0 }}>Find the perfect role in your area of expertise</p>
            </div>
            <Link to="/jobs" className="view-all-btn">
              Explore All Fields
              <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
            </Link>
          </div>

          <div className="categories-grid">
            {categoriesToDisplay.map((cat, i) => {
              let iconChar = "💼";
              if (cat.name.includes("Tech") || cat.name.includes("Software")) iconChar = "💻";
              if (cat.name.includes("Data")) iconChar = "📊";
              if (cat.name.includes("Design")) iconChar = "🎨";
              if (cat.name.includes("Market")) iconChar = "📈";

              return (
              <Link
                key={cat.name}
                to={`/jobs?category=${encodeURIComponent(cat.name)}`}
                className="cat-card reveal"
                style={{ transitionDelay: `${i * 0.05}s` }}
              >
                <div className="cat-icon">
                  {cat.icon || iconChar}
                </div>
                <div className="cat-info">
                  <h4 className="cat-title">{cat.name}</h4>
                  <div className="cat-jobs">{categoryCounts[cat.name] || 0} Open Roles</div>
                </div>
              </Link>
            )})}
          </div>
        </div>
      </section>

      {/* ── FEATURED JOBS SECTION ── */}
      <section className="section-wrapper bg-white-section" id="featured-jobs">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="categories-head reveal">
            <div>
              <h2 className="section-title">Featured <span className="text-teal">Jobs</span></h2>
              <p className="section-sub" style={{ margin: 0 }}>Hand-picked opportunities from top verified companies</p>
            </div>
            <Link to="/jobs" className="view-all-btn">
              View All Jobs
              <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
            </Link>
          </div>

          <div className="jobs-grid">
            {loading ? (
              [...Array(4)].map((_, index) => (
                <div key={index} className="job-glass-card reveal animate-pulse" style={{height: '300px'}}></div>
              ))
            ) : (
              featuredJobs.map((job, i) => (
                <div 
                  key={job._id} 
                  className="job-glass-card reveal"
                  style={{ transitionDelay: `${i * 0.1}s` }}
                >
                  <div className="job-header">
                    <div className="job-logo">
                      {job.company?.name ? job.company.name.charAt(0).toUpperCase() : "C"}
                    </div>
                    <div>
                      <h3 className="job-title">{job.title}</h3>
                      <div className="job-company">{job.company?.name || "Company"}</div>
                    </div>
                  </div>

                  <div className="job-tags">
                    <span className="job-tag job-tag-type">{job.jobType || "Full-time"}</span>
                    {job.location && (
                      <span className="job-tag job-tag-loc">
                        {typeof job.location === 'object' ? (job.location.city || job.location.country || 'Remote') : job.location}
                      </span>
                    )}
                  </div>

                  <p className="job-desc">
                    {job.description ? job.description.substring(0, 100) + '...' : "An exciting opportunity to join our growing team and work on impactful projects."}
                  </p>

                  <div className="job-footer">
                    <div className="job-salary">
                      {job.salaryRange?.min ? `₹${job.salaryRange.min/100000}L - ₹${job.salaryRange.max/100000}L` : "Competitive"}
                    </div>
                    <Link to={`/jobs/${job._id}`} className="job-apply-btn">
                      Apply Now
                    </Link>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS SECTION ── */}
      <section className="section-wrapper bg-gray-section" id="testimonials">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="section-head reveal">
            <h2 className="section-title">What Our <span className="text-teal">Users Say</span></h2>
            <p className="section-sub">Join thousands of professionals who have already accelerated their careers</p>
          </div>

          <div className="testimonials-layout">
            <div className="testimonial-main reveal">
              <div className="t-card">
                <div className="t-quote-icon">"</div>
                <div className="t-rating">{'⭐'.repeat(testimonials[activeTestimonial].rating)}</div>
                <p className="t-text">{testimonials[activeTestimonial].text}</p>
                <div className="t-author">
                  <div className="t-avatar" style={{background: `${testimonials[activeTestimonial].color}20`, color: testimonials[activeTestimonial].color}}>
                    {testimonials[activeTestimonial].avatar}
                  </div>
                  <div>
                    <div className="t-name">{testimonials[activeTestimonial].name}</div>
                    <div className="t-role">{testimonials[activeTestimonial].role} at {testimonials[activeTestimonial].company}</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="t-thumbnails reveal" style={{ transitionDelay: '0.2s' }}>
              {testimonials.map((t, i) => (
                <div
                  key={i}
                  className={`t-thumb ${activeTestimonial === i ? 't-thumb-active' : ''}`}
                  onClick={() => setActiveTestimonial(i)}
                >
                  <div className="t-avatar" style={{width: '45px', height: '45px', background: `${t.color}20`, color: t.color}}>
                    {t.avatar}
                  </div>
                  <div>
                    <div className="t-name" style={{fontSize: '1rem'}}>{t.name}</div>
                    <div className="t-role" style={{fontSize: '0.85rem', color: t.color}}>{t.package}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <FAQSection 
        title="Platform FAQ"
        subtitle="Common questions about using Naukri Platform."
        faqs={[
          { q: 'Is this platform free for job seekers?', a: 'Yes! Our platform is 100% free for job seekers. You can create a profile, search, and apply to unlimited jobs without paying anything.' },
          { q: 'How do employers contact me?', a: 'Employers can message you directly through our in-platform messaging system once you apply, or they can view your contact details if you accept their invitation.' },
          { q: 'How frequently are new jobs added?', a: 'New jobs are posted every single day by top companies around the world.' },
          { q: 'Is my data secure?', a: 'We use industry-standard encryption and security protocols to ensure your resume and personal data are kept completely safe.' },
          { q: 'Can I post jobs as an employer?', a: 'Yes! Simply register as an employer and you can start posting jobs immediately.' }
        ]}
      />
    </div>
  );
};

export default Home;
